import addAdditionalCartData from "./addAdditionalCartData";
import allocateComponents from "./allocateComponents";
import cartRoutes from "./cartRoutes";
import {
  obtainAccessToken,
  generateAccountLoginUrl,
} from "./customerAccountApi";
import fetchProduct from "../../helpers/useProduct/fetchProduct";
import formatDatalayerEcommerce from "../../helpers/formatDatalayerEcommerce";

const persistentCartHeaders = {
  "x-cart-country": window.theme.country,
};

const loginToCustomerAccountsApi = () => {
  generateAccountLoginUrl.then((url) => {
    window.location = url;
  });
};

const obtainAccessTokenPromise = obtainAccessToken().then(
  ({ success, initialLogin }) => {
    if (initialLogin) {
      window.location = `${window.location.origin}/account/login`;
    }
    if (!success) {
      if (window.theme.customer_id) {
        loginToCustomerAccountsApi();
      }

      return;
    }

    try {
      const customerAccessData = localStorage.getItem("customer-access-data");
      const { access_token } = customerAccessData
        ? JSON.parse(customerAccessData)
        : { access_token: null };

      if (access_token && !window.theme.customer_id) {
        console.log(
          "Customer is logged in to the Customer Accounts API but not the Liquid storefront",
        );
        // Customer is logged in to the Customer Accounts API but not the Liquid storefront
        // redirect to trigger login to Liquid storefront
        window.location = `${window.location.origin}/account/login`;
        return;
      } else if (window.theme.customer_id && !access_token) {
        console.log(
          `Customer is logged in to the Liquid storefront but not to Customer Account API`,
        );
        // Customer is logged in to the Liquid storefront but not to Customer Account API
        // redirect user to log in to Customer Accounts API
        loginToCustomerAccountsApi();
      }

      const companyLocationId = window.theme.location_id
        ? `gid://shopify/CompanyLocation/${window.theme.location_id}`
        : undefined;

      if (access_token) {
        persistentCartHeaders["x-cart-access-token"] = access_token;
      }

      if (companyLocationId) {
        persistentCartHeaders["x-cart-company-location-id"] = companyLocationId;
      }

      return access_token;
    } catch (error) {
      console.warn("Error obtaining access token", error);
    }
  },
);

window.cartData = {};
let latestUpdateId = 0;

const updateCartData = (newCartData) => {
  latestUpdateId++;
  const updateId = latestUpdateId;
  return addAdditionalCartData(newCartData).then((newCartData) => {
    if (updateId !== latestUpdateId) return;

    window.cartData = allocateComponents(newCartData);
    console.log(`cartData`, cartData);

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { cartData: window.cartData },
      }),
    );
  });
};

window.loadInitialCart = obtainAccessTokenPromise
  .then(() =>
    fetch(cartRoutes.cartUrl, {
      headers: {
        ...persistentCartHeaders,
      },
    }),
  )
  .then((res) => res.json())
  .then(updateCartData);

window.reloadCart = () => {
  obtainAccessTokenPromise
    .then(() =>
      fetch(cartRoutes.cartUrl, {
        headers: {
          ...persistentCartHeaders,
        },
      }),
    )
    .then((res) => res.json())
    .then((data) => {
      updateCartData(data);
    });
};

/**
 * Add an item to cart
 * @param {Object|Array} items [{ id, quantity, properties }]
 * @param {Object} options object of callbacks
 */
window.addToCart = (items, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }
  if (!Array.isArray(items)) {
    items = [items];
  }
  const data = {
    items: [...items],
  };

  obtainAccessTokenPromise
    .then(() =>
      fetch(cartRoutes.cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...persistentCartHeaders,
        },
        body: JSON.stringify(data),
      }),
    )
    .then((response) => {
      if (response.ok) {
        return response.json();
      }

      throw new Error("Cart error", response);
    })
    .then((data) => {
      updateCartData(data);

      if (options?.onAfterAdd) {
        options.onAfterAdd(data);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError();
      }
      console.error(error);
    });
};

/**
 * Change an item in the cart
 * @param {Object|Array} items [{ id, quantity, properties }]
 * @param {Object} options object of callbacks
 */
window.changeCart = (change, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  obtainAccessTokenPromise
    .then(() =>
      fetch(cartRoutes.cartChangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...persistentCartHeaders,
        },
        body: JSON.stringify({ ...change }),
      }),
    )
    .then((response) => {
      if (response.ok) {
        return response.json();
      }

      throw new Error("Cart error", response);
    })
    .then((data) => {
      updateCartData(data);

      if (options?.onAfterAdd) {
        options.onAfterAdd(data);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError();
      }
      console.error(error);
    });
};

/**
 * Update cart
 * @param {Object} updateInput { attributes, note, updates: { id: quantity } }
 * @param {Object} options object of callbacks
 */
window.updateCart = ({ attributes, note, updates }, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  const body = {};

  if (attributes) {
    body.attributes = {
      ...(cartData.attributes || {}),
      // __storefront_cart_id: cartData.id,
      ...attributes,
    };
  }

  if (note) {
    body.name = note;
  }

  if (updates) {
    Object.entries(updates).forEach(([id, quantity]) => {
      const item = cartData.items.find((item) => item.id === id);

      if (!item) return;

      const productId = `gid://shopify/Product/${item.product_id}`;
      const key = quantity > item.quantity ? "add" : "remove";
      const event = key === "add" ? "dl_add_to_cart" : "dl_remove_from_cart";

      fetchProduct(productId).then((product) => {
        const variantId = `gid://shopify/ProductVariant/${item.variant_id}`;
        const variant = product.variants.find(
          (variant) => variant.id === variantId,
        );

        const customProperties = {
          quantity: `${Math.abs(quantity - item.quantity)}`,
        };

        if (key === "add") {
          (customProperties.is_quick_view = false),
            (customProperties.is_product_set = false);
        }

        const ecommerce = formatDatalayerEcommerce({
          product: product,
          variant,
          key,
          customProperties,
        });

        window.ElevarDataLayer?.push({
          event,
          ecommerce,
        });
      });
    });

    body.updates = updates;
  }

  obtainAccessTokenPromise
    .then(() =>
      fetch(cartRoutes.cartUpdateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...persistentCartHeaders,
        },
        body: JSON.stringify(body),
      }),
    )
    .then((response) => {
      if (response.ok) {
        return response.json();
      }

      throw new Error("Cart error", response);
    })
    .then((data) => {
      updateCartData(data);

      if (options?.onAfterAdd) {
        options.onAfterAdd(data);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError();
      }
      console.error(error);
    });
};

/**
 * Clear cart
 */
window.clearCart = () => {
  obtainAccessTokenPromise
    .then(() =>
      fetch(cartRoutes.cartClearUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...persistentCartHeaders,
        },
      }),
    )
    .then((response) => {
      if (response.ok) {
        return response.json();
      }

      throw new Error("Cart error", response);
    })
    .then((data) => {
      updateCartData(data);
    })
    .catch((error) => {
      console.error(error);
    });
};

/**
 * Save quote
 * @param {Object} options object of callbacks
 */
window.saveQuote = (body = {}, options = {}) => {
  if (options?.onBeforeSave) {
    options.onBeforeSave();
  }

  obtainAccessTokenPromise
    .then(() =>
      fetch(cartRoutes.saveQuoteUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...persistentCartHeaders,
        },
        body: JSON.stringify(body),
      }),
    )
    .then((response) => {
      if (response.ok) {
        return response.text();
      }

      throw new Error("Cart error", response);
    })
    .then((data) => {
      if (options?.onAfterSave) {
        options.onAfterSave(data);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError();
      }
      console.error(error);
    });
};
